const palette = {
    bili: "#f3c84b",
    fgf: "#7bd88f",
    mineral: "#75b7ff",
    kidney: "#91a7ff",
    liver: "#ff9e64",
    blood: "#ff6b8a",
    stats: "#d7dce2",
    tech: "#b4ffef"
};
function choice(id, label, domain, icon, color, blurb) {
    return { id, label, domain, icon, color, blurb };
}
const C = {
    animalId: choice("animal_id", "Animal ID", "metadata", "#", palette.tech, "Stable key before any joining."),
    duplicateId: choice("duplicate_id", "Duplicate ID", "qc", "!!", palette.tech, "One animal appears twice."),
    missingUnit: choice("missing_unit", "Missing unit", "qc", "?", palette.tech, "A value without a unit is a trap."),
    impossibleValue: choice("impossible_value", "Impossible value", "qc", "X", palette.tech, "A lab value outside plausible range."),
    bilirubin: choice("bilirubin", "Bilirubin", "exposure", "Bi", palette.bili, "Primary exposure candidate."),
    fgf23: choice("fgf23", "FGF23", "outcome", "F23", palette.fgf, "Primary outcome candidate."),
    phosphate: choice("phosphate", "Phosphate", "mineral", "P", palette.mineral, "Core FGF23 biology anchor."),
    calcium: choice("calcium", "Calcium", "mineral", "Ca", palette.mineral, "Mineral metabolism context."),
    vitaminD: choice("vitamin_d", "Vitamin D", "mineral", "D", palette.mineral, "Regulatory context for FGF23."),
    pth: choice("pth", "PTH", "mineral", "PTH", palette.mineral, "Endocrine mineral axis."),
    creatinine: choice("creatinine", "Creatinine", "kidney", "Cr", palette.kidney, "Kidney function proxy."),
    urea: choice("urea", "Urea/BUN", "kidney", "U", palette.kidney, "Kidney and metabolism context."),
    alt: choice("alt", "ALT", "liver", "ALT", palette.liver, "Liver injury/context marker."),
    albumin: choice("albumin", "Albumin", "liver", "Alb", palette.liver, "Binding and liver context."),
    hemoglobin: choice("hemoglobin", "Hemoglobin", "blood", "Hb", palette.blood, "Anemia context."),
    inflammation: choice("inflammation", "Inflammation", "immune", "IL", palette.blood, "Inflammatory signal."),
    weight: choice("weight", "Body weight", "animal", "g", palette.stats, "Animal size and maturity clue."),
    sex: choice("sex", "Sex", "animal", "S", palette.stats, "Possible baseline and interaction factor."),
    batch: choice("batch", "Assay batch", "technical", "B", palette.tech, "Technical artifact candidate."),
    logScale: choice("log_scale", "Log scale", "statistics", "log", palette.stats, "Often helps skewed biomarkers."),
    fdr: choice("fdr", "FDR control", "statistics", "FDR", palette.stats, "Controls false discoveries."),
    effectSize: choice("effect_size", "Effect size", "statistics", "ES", palette.stats, "Magnitude matters."),
    ci: choice("confidence_interval", "Confidence interval", "statistics", "CI", palette.stats, "Uncertainty is part of the result."),
    residuals: choice("residuals", "Residual checks", "statistics", "R", palette.stats, "Assumptions need inspection."),
    askSteffen: choice("ask_steffen", "Ask Steffen", "workflow", "@", palette.tech, "When metadata are unclear, ask.")
};
export const chapters = [
    {
        id: "booting-the-lab",
        title: "Booting The Lab",
        subtitle: "The CRT wakes up and the dataset blinks back.",
        objective: "Identify the master data clues before the lab computer panics.",
        narrative: "Steffen has sent files. The old PC insists that science starts with metadata.",
        badge: "Floppy of Orientation",
        journalUnlock: "A good analysis begins by knowing which file, row, and variable mean what.",
        tasks: [
            {
                id: "boot-master-keys",
                prompt: "Which clue must be stable before joining sheets?",
                brief: "The spreadsheet has several tabs and one suspiciously cheerful macro button.",
                choices: [C.animalId, C.bilirubin, C.fgf23, C.batch],
                correctIds: ["animal_id"],
                hint: "Without this, two sheets can quietly describe different animals.",
                success: "Animal ID locked. The spreadsheet stops making dial-up noises.",
                failure: "Close, but first we need the key that makes every row traceable.",
                concept: "Stable identifiers protect every downstream merge.",
                score: 100
            },
            {
                id: "boot-primary-pair",
                prompt: "Which two variables define the original simple question?",
                brief: "The naive test was simple, maybe too simple.",
                choices: [C.bilirubin, C.fgf23, C.phosphate, C.sex, C.batch],
                correctIds: ["bilirubin", "fgf23"],
                hint: "Exposure and outcome. Yellow clue meets bone hormone clue.",
                success: "Bilirubin vs. FGF23 loaded. The PC whispers: have you tried biology?",
                failure: "Those may matter later, but the original question had one exposure and one outcome.",
                concept: "Name the primary question before exploring everything else.",
                score: 120
            },
            {
                id: "boot-metadata-gap",
                prompt: "What should you do when assay metadata are unclear?",
                brief: "The FGF23 kit type is missing from the file notes.",
                choices: [C.askSteffen, C.fdr, C.effectSize, C.logScale],
                correctIds: ["ask_steffen"],
                hint: "A missing assay note is not solved by a prettier model.",
                success: "Message queued: ask for assay type, units, and group definitions.",
                failure: "Statistics cannot rescue unknown measurement meaning.",
                concept: "Unknown assay details are a data intake question, not a modeling trick.",
                score: 100
            }
        ]
    },
    {
        id: "qc-quest",
        title: "QC Quest",
        subtitle: "A dungeon of missing units and suspicious rows.",
        objective: "Clean enough to model without stepping on a rake.",
        narrative: "The lab bench is covered in post-its. Some are helpful. Some are just yellow.",
        badge: "Unit Wrangler",
        journalUnlock: "QC is not admin work; it is the first scientific result.",
        tasks: [
            {
                id: "qc-stop-signs",
                prompt: "Which issues must stop modeling until reviewed?",
                brief: "The PC opens a red dialog called ABSOLUTELY_NOT_FINAL.xls.",
                choices: [C.duplicateId, C.missingUnit, C.impossibleValue, C.effectSize, C.ci],
                correctIds: ["duplicate_id", "missing_unit", "impossible_value"],
                hint: "Look for things that can corrupt rows or meaning.",
                success: "QC alarms caught. The lab computer gives you one respectful beep.",
                failure: "A model can be precise and still be precisely wrong.",
                concept: "IDs, units, and plausible ranges come before inference.",
                score: 150
            },
            {
                id: "qc-technical",
                prompt: "Which variable can create a technical pattern that looks biological?",
                brief: "Some samples were measured on different plates.",
                choices: [C.batch, C.bilirubin, C.weight, C.effectSize],
                correctIds: ["batch"],
                hint: "The plate has entered the chat.",
                success: "Batch effect flagged. The spreadsheet stops pretending it is a pathway.",
                failure: "Biology matters, but this clue is about the measuring process.",
                concept: "Batch effects can mimic biology if ignored.",
                score: 100
            },
            {
                id: "qc-outlier-response",
                prompt: "A plausible extreme value appears. What belongs in the workflow?",
                brief: "One FGF23 value towers over the rest like a tiny boss fight.",
                choices: [C.residuals, C.askSteffen, C.logScale, C.fdr],
                correctIds: ["residuals", "ask_steffen", "log_scale"],
                hint: "Do not delete first. Inspect, document, and test sensitivity.",
                success: "Outlier handled like science, not panic.",
                failure: "The correct move is review plus sensitivity, not instant deletion.",
                concept: "Extreme values deserve diagnostics and documentation.",
                score: 140
            }
        ]
    },
    {
        id: "scatterplot-arcade",
        title: "Scatterplot Arcade",
        subtitle: "Insert coin. Receive humility.",
        objective: "Learn what the simple plot can and cannot tell you.",
        narrative: "The arcade machine says CORRELATION QUEST. It is probably overconfident.",
        badge: "Axis Tamer",
        journalUnlock: "A simple plot is a window, not the whole lab.",
        tasks: [
            {
                id: "scatter-axis",
                prompt: "Which axis pair shows the original bivariate check?",
                brief: "The arcade wants x and y tokens.",
                choices: [C.bilirubin, C.fgf23, C.phosphate, C.creatinine],
                correctIds: ["bilirubin", "fgf23"],
                hint: "Original exposure on one axis, outcome on the other.",
                success: "Scatterplot appears. It refuses to explain the universe.",
                failure: "Those variables may be useful, but not as the original axis pair.",
                concept: "Start simple, then ask what the simple view misses.",
                score: 100
            },
            {
                id: "scatter-transform",
                prompt: "Which tool often helps when biomarkers are skewed?",
                brief: "The points are huddled in one corner like they know something.",
                choices: [C.logScale, C.fdr, C.batch, C.animalId],
                correctIds: ["log_scale"],
                hint: "Think scale, not significance.",
                success: "Log view unlocked. The points finally use the whole arcade cabinet.",
                failure: "Multiple testing and batch matter elsewhere; this one is about shape.",
                concept: "Transformations can make relationships and diagnostics readable.",
                score: 90
            },
            {
                id: "scatter-color",
                prompt: "Which overlays can reveal hidden structure in a scatterplot?",
                brief: "The arcade offers colored joystick buttons.",
                choices: [C.sex, C.batch, C.weight, C.fdr],
                correctIds: ["sex", "batch"],
                hint: "Color groups that can split the cloud into stories.",
                success: "Groups toggled. The plot starts gossiping responsibly.",
                failure: "A scoreboard is not a grouping variable.",
                concept: "Coloring by sex or batch can reveal subgroup and technical patterns.",
                score: 120
            }
        ]
    },
    {
        id: "pathway-pinboard",
        title: "Pathway Pinboard",
        subtitle: "String, sticky notes, and suspiciously tidy biology.",
        objective: "Map FGF23 into its biological neighborhood.",
        narrative: "The pinboard demands a pathway, not a p-value shopping spree.",
        badge: "Pathway Cartographer",
        journalUnlock: "FGF23 should be interpreted inside mineral, kidney, and systemic context.",
        tasks: [
            {
                id: "pathway-mineral-axis",
                prompt: "Which cards belong to the mineral metabolism axis?",
                brief: "FGF23 points at a blue folder labeled DO NOT IGNORE.",
                choices: [C.phosphate, C.calcium, C.vitaminD, C.pth, C.alt, C.batch],
                correctIds: ["phosphate", "calcium", "vitamin_d", "pth"],
                hint: "Think phosphate handling and endocrine mineral regulation.",
                success: "Mineral axis mapped. The pinboard glows professionally.",
                failure: "Liver and technical cards matter, but this task asks for mineral biology.",
                concept: "FGF23 is deeply tied to phosphate, calcium, vitamin D, and PTH.",
                score: 180
            },
            {
                id: "pathway-kidney",
                prompt: "Which markers help frame kidney contribution?",
                brief: "A tiny kidney icon has printed a receipt.",
                choices: [C.creatinine, C.urea, C.albumin, C.fdr, C.hemoglobin],
                correctIds: ["creatinine", "urea"],
                hint: "Look for filtration/metabolic waste clues.",
                success: "Kidney context connected. The receipt is mostly legible.",
                failure: "Those may still matter, but kidney context has specific markers.",
                concept: "Kidney function can shape mineral metabolism and FGF23 interpretation.",
                score: 120
            },
            {
                id: "pathway-systemic",
                prompt: "Which systemic signals might connect inflammation or anemia to FGF23?",
                brief: "The old PC opens a file named BLOOD_COUNT_MYSTERY.wav.",
                choices: [C.inflammation, C.hemoglobin, C.batch, C.animalId, C.bilirubin],
                correctIds: ["inflammation", "hemoglobin"],
                hint: "Think immune signal and blood oxygen-carrying context.",
                success: "Systemic context unlocked. The WAV file was only one beep.",
                failure: "Batch and IDs are important, but not the systemic biology asked here.",
                concept: "Inflammation and anemia-like signals can be relevant around FGF23.",
                score: 130
            }
        ]
    },
    {
        id: "confounder-castle",
        title: "Confounder Castle",
        subtitle: "Every door is labeled plausible. Rude.",
        objective: "Separate variable roles before building models.",
        narrative: "The castle drawbridge only lowers for disciplined causal guesses.",
        badge: "Collider Dodger",
        journalUnlock: "Variables have roles. Treating every variable the same is how castles eat models.",
        tasks: [
            {
                id: "confounder-basics",
                prompt: "Which cards are plausible adjustment candidates before exploring fancy models?",
                brief: "The castle asks for boring-but-important context.",
                choices: [C.sex, C.weight, C.batch, C.fdr, C.effectSize],
                correctIds: ["sex", "weight", "batch"],
                hint: "Animal context and technical context, not reporting outputs.",
                success: "Drawbridge down. The castle respects practical covariates.",
                failure: "Effect size and FDR describe results; they are not adjustment variables.",
                concept: "Sex, weight, and batch can shape interpretation before deeper biology.",
                score: 140
            },
            {
                id: "confounder-interaction",
                prompt: "Which variable is a plausible effect modifier to check for Bilirubin-FGF23 differences?",
                brief: "Two doors appear: same effect everywhere, or maybe not.",
                choices: [C.sex, C.animalId, C.fdr, C.duplicateId],
                correctIds: ["sex"],
                hint: "Could the relationship differ between groups?",
                success: "Interaction gate opened. The castle mutters about sample size.",
                failure: "IDs and FDR do not define a biological subgroup effect.",
                concept: "Sex can be tested as an effect modifier if sample size permits.",
                score: 110
            },
            {
                id: "confounder-caution",
                prompt: "What warning belongs before adding every available blood parameter?",
                brief: "The model hallway stretches suspiciously far.",
                choices: [C.effectSize, C.residuals, C.fdr, C.impossibleValue],
                correctIds: ["residuals", "fdr"],
                hint: "Too many tests and assumptions need guardrails.",
                success: "The hallway shrinks. The model is now wearing only one lab coat.",
                failure: "Quality issues matter, but this warning is about analysis discipline.",
                concept: "Exploration needs diagnostics and multiple-testing control.",
                score: 120
            }
        ]
    },
    {
        id: "model-machine",
        title: "Model Machine",
        subtitle: "Feed it cards. Stop before it eats the project.",
        objective: "Build small defensible models and read them properly.",
        narrative: "The machine loves predictors. You must be the adult in the room.",
        badge: "Overfit Bouncer",
        journalUnlock: "A small model with a clear reason beats a large model with a dramatic p-value.",
        tasks: [
            {
                id: "model-primary",
                prompt: "What belongs in a minimal first adjusted model?",
                brief: "The machine asks for the first serious recipe.",
                choices: [C.bilirubin, C.fgf23, C.sex, C.weight, C.phosphate, C.inflammation],
                correctIds: ["bilirubin", "fgf23", "sex", "weight"],
                hint: "Outcome, exposure, and basic animal context.",
                success: "Minimal model assembled. It looks pleasantly undramatic.",
                failure: "Do not skip the primary pair or overload the first model.",
                concept: "Start with a small pre-specified adjustment set.",
                score: 160
            },
            {
                id: "model-domain",
                prompt: "Which domain card should be considered early because of FGF23 biology?",
                brief: "The machine points at a glowing mineral folder.",
                choices: [C.phosphate, C.alt, C.batch, C.animalId],
                correctIds: ["phosphate"],
                hint: "FGF23 and phosphate are not strangers.",
                success: "Phosphate domain queued. The machine hums in a more biological key.",
                failure: "Good variables exist elsewhere, but FGF23 has a mineral favorite.",
                concept: "Phosphate is a central FGF23 biology anchor.",
                score: 120
            },
            {
                id: "model-reporting",
                prompt: "Which outputs should you report beyond a p-value?",
                brief: "The machine prints a p-value in 72-point font. Suspicious.",
                choices: [C.effectSize, C.ci, C.residuals, C.duplicateId],
                correctIds: ["effect_size", "confidence_interval", "residuals"],
                hint: "Magnitude, uncertainty, and diagnostics.",
                success: "Report upgraded. The p-value shrinks to a reasonable font size.",
                failure: "QC issues matter earlier; model reporting needs magnitude and uncertainty.",
                concept: "Effect sizes, intervals, and diagnostics make results interpretable.",
                score: 160
            }
        ]
    },
    {
        id: "multiplicity-mayhem",
        title: "Multiplicity Mayhem",
        subtitle: "Twenty tests enter. Fewer claims leave.",
        objective: "Control exploratory excitement before it writes the discussion.",
        narrative: "Pop-up windows spawn every possible association. The lab computer sweats.",
        badge: "FDR Firefighter",
        journalUnlock: "Exploration is powerful when families of tests are labeled and false discoveries are controlled.",
        tasks: [
            {
                id: "multi-family",
                prompt: "What should you define before correcting exploratory p-values?",
                brief: "The pop-ups demand a family meeting.",
                choices: [C.fdr, C.effectSize, C.phosphate, C.animalId],
                correctIds: ["fdr"],
                hint: "The correction method belongs here.",
                success: "FDR panel opened. The pop-ups stand in a more orderly line.",
                failure: "The correction method is the key tool for this chaos.",
                concept: "False discovery rate control helps keep exploratory testing honest.",
                score: 120
            },
            {
                id: "multi-priority",
                prompt: "Which evidence should prioritize a follow-up signal?",
                brief: "Three shiny associations wave at you from the screen.",
                choices: [C.effectSize, C.ci, C.fdr, C.impossibleValue],
                correctIds: ["effect_size", "confidence_interval", "fdr"],
                hint: "Signal strength, uncertainty, and multiplicity all matter.",
                success: "Follow-up signal prioritized without p-value fever.",
                failure: "A plausible data error is not evidence strength.",
                concept: "Robust exploratory signals need magnitude, uncertainty, and FDR context.",
                score: 150
            },
            {
                id: "multi-language",
                prompt: "Which wording is safest for an exploratory finding?",
                brief: "The discussion paragraph is trying to say PROVES in all caps.",
                choices: [
                    choice("suggests", "Suggests / may indicate", "language", "may", palette.stats, "Cautious claim."),
                    choice("proves", "Proves", "language", "!!", palette.blood, "Too strong for exploration."),
                    choice("hypothesis", "Hypothesis-generating", "language", "H", palette.stats, "Correct strength."),
                    C.fdr
                ],
                correctIds: ["suggests", "hypothesis"],
                hint: "Exploratory language should keep both feet on the floor.",
                success: "Discussion defused. Peer review lowers one eyebrow.",
                failure: "Exploratory data should not be promoted to proof.",
                concept: "Cautious wording protects the science.",
                score: 130
            }
        ]
    },
    {
        id: "peer-review-finale",
        title: "Peer Review Finale",
        subtitle: "The final boss has comments.",
        objective: "Assemble the Research Journal into a cautious next-step plan.",
        narrative: "Steffen, the old PC, and a skeptical reviewer gather around the CRT.",
        badge: "Reviewer Whisperer",
        journalUnlock: "The win condition is a defensible next analysis, not a louder claim.",
        tasks: [
            {
                id: "final-primary",
                prompt: "What must be separated in the final summary?",
                brief: "The journal has too many sticky notes and one dramatic arrow.",
                choices: [
                    choice("primary", "Primary question", "reporting", "1", palette.stats, "Pre-specified focus."),
                    choice("exploratory", "Exploratory signals", "reporting", "?", palette.stats, "Hypothesis-generating."),
                    C.batch,
                    C.duplicateId
                ],
                correctIds: ["primary", "exploratory"],
                hint: "Do not mix confirmed focus and exploration into one soup.",
                success: "Journal sorted. The reviewer writes 'clearer' and moves on.",
                failure: "The summary needs claim-strength categories.",
                concept: "Primary and exploratory findings must be labeled separately.",
                score: 140
            },
            {
                id: "final-next-step",
                prompt: "Which next step best prepares the real analysis?",
                brief: "The final screen asks what happens after the game.",
                choices: [C.askSteffen, C.animalId, C.fdr, C.logScale],
                correctIds: ["ask_steffen", "animal_id"],
                hint: "Get metadata and stable structure before deeper modeling.",
                success: "Next-step plan written. The CRT gives a tiny standing ovation.",
                failure: "Methods matter, but first the real data intake must be complete.",
                concept: "The real project needs data intake, dictionary, and analysis plan alignment.",
                score: 130
            },
            {
                id: "final-win-condition",
                prompt: "What is the scientific win condition?",
                brief: "The old computer asks for one final answer.",
                choices: [
                    choice("better_question", "A better next question", "science", "?", palette.fgf, "Actionable science."),
                    choice("single_p", "One magic p-value", "science", "p", palette.blood, "Tempting but weak."),
                    C.effectSize,
                    C.ci
                ],
                correctIds: ["better_question", "effect_size", "confidence_interval"],
                hint: "A conclusion needs direction, magnitude, and uncertainty.",
                success: "Gunnrats23 complete. The lab computer finally saves without crashing.",
                failure: "The game is not won by one magic p-value.",
                concept: "Good analysis produces defensible insight and the next smarter question.",
                score: 180
            }
        ]
    }
];
export const playerRoles = [
    "QC Scout",
    "Pathway Mapper",
    "Model Builder",
    "Skeptic",
    "Archivist"
];
export const playerColors = ["#7bd88f", "#75b7ff", "#f3c84b", "#ff9e64", "#ff6b8a", "#b4ffef"];
